import { PhotoCaptureField } from "@/components/inspection/photo-capture-field";
import { UrlAnswerField } from "@/components/url-answer-field";
import { UserType } from "@/constants";
import {
  getPreviousPropertyManagersOptions,
  getQuestionOptionsOptions,
} from "@/hera-api/client/@tanstack/react-query.gen";
import type { LicenseQuestionAnswerType } from "@/hera-api/client/types.gen";
import { documentAnswerTokens } from "@/lib/answer-file";
import {
  type ContactRequirementValues,
  deriveAnswerRequiredForContactQuestion,
  nacFieldRequirementsForValidation,
} from "@/lib/contact-question-requirements";
import { isNacStyleAnswerType } from "@/lib/nac-answer-types";
import { hasPhotoAnswers } from "@/lib/photo-answer";
import { getVisibleQuestionsWithJumpLogic } from "@/lib/question-jump-logic";
import {
  getRegistrantDisplayAddress1,
  getRegistrantDisplayAddress2,
  getRegistrantDisplayCity,
  getRegistrantDisplayCompanyName,
  getRegistrantDisplayName,
  getRegistrantDisplayState,
  getRegistrantDisplayZipcode,
} from "@/lib/registrant-display";
import { urlAnswerSchema } from "@/lib/url-answer";
import { cn, formatPhoneNumber } from "@/lib/utils";
import { useQuery } from "@tanstack/react-query";
import { format, parse } from "date-fns";
import { Upload as UploadIcon } from "lucide-react";
import type React from "react";
import { useEffect, useId, useMemo, useState } from "react";
import { z } from "zod";
import { usePlacesAutocomplete } from "../../../hooks/use-places-autocomplete";
import { parseGoogleAddressComponents } from "../../../lib/parse-google-address-components";
import { Button } from "../../ui/button";
import { Checkbox } from "../../ui/checkbox";
import { DatePicker } from "../../ui/date-picker";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import { PartySelect } from "../../ui/party-select";
import { RadioGroup, RadioGroupItem } from "../../ui/radio-group";
import { RichTextHtml } from "../../ui/rich-text-html";
import { Select } from "../../ui/select";
import { SuggestionList } from "../../ui/suggestion-list";

interface PreviousContactSuggestion {
  company_name?: string | null;
  name?: string | null;
  email?: string | null;
  phone?: string | null;
  address1?: string | null;
  address2?: string | null;
  city?: string | null;
  state?: string | null;
  zipcode?: string | null;
}

function previousContactMatches(
  contact: PreviousContactSuggestion,
  query: string,
): boolean {
  const needle = query.trim().toLowerCase();
  if (!needle) return true;
  return [contact.company_name, contact.name].some((value) =>
    (value ?? "").toLowerCase().includes(needle),
  );
}

function PreviousContactAutocomplete({
  contacts,
  labelBy,
  query,
  onSelect,
  children,
}: {
  contacts: PreviousContactSuggestion[];
  labelBy: "company" | "contact";
  query: string;
  onSelect: (contact: PreviousContactSuggestion) => void;
  children: React.ReactElement<{
    onKeyDown?: (event: React.KeyboardEvent) => void;
    onFocus?: (event: React.FocusEvent) => void;
    onBlur?: (event: React.FocusEvent) => void;
  }>;
}) {
  const [hiddenQuery, setHiddenQuery] = useState<string | null>(null);
  const matches = contacts.filter((contact) =>
    previousContactMatches(contact, query),
  );
  return (
    <SuggestionList
      closeOnBlur
      suggestions={hiddenQuery === query ? [] : matches}
      getSuggestionKey={(contact) =>
        [
          contact.company_name,
          contact.name,
          contact.email,
          contact.address1,
        ].join("|")
      }
      renderSuggestion={(contact) => (
        <PreviousContactSuggestionRow contact={contact} labelBy={labelBy} />
      )}
      onSuggestionSelect={(contact) => {
        const company = contact.company_name?.trim() ?? "";
        const person = contact.name?.trim() ?? "";
        setHiddenQuery(labelBy === "company" ? company : person);
        onSelect(contact);
      }}
    >
      {children}
    </SuggestionList>
  );
}

function PreviousContactSuggestionRow({
  contact,
  labelBy,
}: {
  contact: PreviousContactSuggestion;
  labelBy: "company" | "contact";
}) {
  const company = contact.company_name?.trim() ?? "";
  const person = contact.name?.trim() ?? "";
  const primary = labelBy === "company" ? company || person : person || company;
  const secondary = labelBy === "company" ? person : company;
  return (
    <span className="flex flex-col py-0.5">
      <span>{primary || "—"}</span>
      {secondary && secondary !== primary ? (
        <span className="text-xs opacity-80">{secondary}</span>
      ) : null}
    </span>
  );
}

// Document upload component
interface DocumentUploadProps {
  field: any;
  handleChange: (value: any) => void;
  questionName?: string;
  forceValidation?: boolean;
  allowMultiple?: boolean;
}

function DocumentUploadField({
  field,
  handleChange,
  questionName,
  forceValidation,
  allowMultiple = false,
}: DocumentUploadProps) {
  if (allowMultiple) {
    return (
      <DocumentMultiUploadField
        field={field}
        handleChange={handleChange}
        questionName={questionName}
        forceValidation={forceValidation}
      />
    );
  }
  return (
    <DocumentSingleUploadField
      field={field}
      handleChange={handleChange}
      questionName={questionName}
      forceValidation={forceValidation}
    />
  );
}

function DocumentMultiUploadField({
  field,
  handleChange,
  questionName,
  forceValidation,
}: DocumentUploadProps) {
  const [isUploading, setIsUploading] = useState(false);
  const [isDragActive, setIsDragActive] = useState(false);
  const inputId = `file-upload-${questionName || "document"}`;
  const tokens = documentAnswerTokens(field.state.value);

  const uploadFiles = async (files: File[]) => {
    if (files.length === 0) return;
    setIsUploading(true);
    const next = [...tokens];
    try {
      const { uploadAnswerFile } = await import("@/hera-api/client/sdk.gen");
      for (const file of files) {
        const resp = await uploadAnswerFile({ body: { file } });
        const token = (resp as { data?: { token?: string } }).data?.token;
        if (!token) {
          throw new Error("Upload failed: no token returned");
        }
        next.push(token);
      }
      handleChange(next);
    } catch (error) {
      console.error("File upload error:", error);
      handleChange(next.length > 0 ? next : null);
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className="space-y-2">
      <div
        className={`border-2 border-dashed rounded-md p-6 text-center transition-colors ${
          isDragActive ? "border-primary bg-primary/10" : "border-muted"
        }`}
        onDragOver={(e) => {
          e.preventDefault();
          setIsDragActive(true);
        }}
        onDragLeave={(e) => {
          e.preventDefault();
          setIsDragActive(false);
        }}
        onDrop={(e) => {
          e.preventDefault();
          setIsDragActive(false);
          void uploadFiles(Array.from(e.dataTransfer.files));
        }}
      >
        <div className="flex flex-col items-center gap-2">
          <UploadIcon className="h-8 w-8 text-muted-foreground mb-2" />
          <span className="font-medium">
            Drag and drop sketches or photos here
          </span>
          <span className="text-xs text-muted-foreground">
            A document or a photo is fine. You can add more than one.
          </span>
          <label htmlFor={inputId} className="inline-block cursor-pointer">
            <input
              id={inputId}
              type="file"
              multiple
              className="hidden"
              disabled={isUploading}
              onChange={(e) => {
                const files = Array.from(e.target.files ?? []);
                e.target.value = "";
                void uploadFiles(files);
              }}
            />
            <span className="underline text-primary">
              {isUploading ? "Uploading..." : "Choose files"}
            </span>
          </label>
        </div>
      </div>
      {tokens.map((token, index) => (
        <div key={token} className="border rounded-md p-4 bg-muted/50">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium">
              File {index + 1} uploaded
            </span>
            <Button
              variant="outline"
              size="sm"
              type="button"
              onClick={() => {
                const next = tokens.filter((item) => item !== token);
                handleChange(next.length > 0 ? next : null);
              }}
            >
              Remove
            </Button>
          </div>
        </div>
      ))}
      {forceValidation &&
        field.state.errors &&
        field.state.errors.length > 0 && (
          <p className="text-destructive text-sm">{field.state.errors[0]}</p>
        )}
    </div>
  );
}

function DocumentSingleUploadField({
  field,
  handleChange,
  questionName,
  forceValidation,
}: DocumentUploadProps) {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadToken, setUploadToken] = useState<string | null>(null);
  const [isDragActive, setIsDragActive] = useState(false);

  // Load existing token from field value if present
  useEffect(() => {
    const currentValue = field.state.value as string | null | undefined;
    if (currentValue && typeof currentValue === "string") {
      setUploadToken(currentValue);
    }
  }, [field.state.value]);

  const handleFileSelect = async (file: File) => {
    setSelectedFile(file);
    setIsUploading(true);
    try {
      const { uploadAnswerFile } = await import("@/hera-api/client/sdk.gen");
      const resp = await uploadAnswerFile({
        body: { file },
      });
      const token = (resp as any).data?.token as string | undefined;
      if (token) {
        setUploadToken(token);
        handleChange(token);
      } else {
        throw new Error("Upload failed: no token returned");
      }
    } catch (error: any) {
      console.error("File upload error:", error);
      setSelectedFile(null);
      setUploadToken(null);
      handleChange(null);
    } finally {
      setIsUploading(false);
    }
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragActive(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragActive(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragActive(false);
    const file = e.dataTransfer.files[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleRemove = () => {
    setSelectedFile(null);
    setUploadToken(null);
    handleChange(null);
    // Reset the file input
    if (questionName) {
      const fileInput = document.getElementById(
        `file-upload-${questionName}`,
      ) as HTMLInputElement;
      if (fileInput) {
        fileInput.value = "";
      }
    }
  };

  // If we have a token, show the file info
  if (uploadToken && !selectedFile) {
    return (
      <div className="space-y-2">
        <div className="border rounded-md p-4 bg-muted/50">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <UploadIcon className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm font-medium">
                Document uploaded successfully
              </span>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={handleRemove}
              type="button"
            >
              Remove
            </Button>
          </div>
        </div>
        {forceValidation &&
          field.state.errors &&
          field.state.errors.length > 0 && (
            <p className="text-destructive text-sm">{field.state.errors[0]}</p>
          )}
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {!selectedFile && !isUploading && (
        <div
          className={`border-2 border-dashed rounded-md p-6 text-center transition-colors ${
            isDragActive ? "border-primary bg-primary/10" : "border-muted"
          }`}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          <div className="flex flex-col items-center gap-2">
            <UploadIcon className="h-8 w-8 text-muted-foreground mb-2" />
            <span className="font-medium">Drag and drop a file here</span>
            <span className="text-xs text-muted-foreground">or</span>
            <label
              htmlFor={`file-upload-${questionName || "document"}`}
              className="inline-block cursor-pointer"
            >
              <input
                id={`file-upload-${questionName || "document"}`}
                type="file"
                className="hidden"
                onChange={handleFileInputChange}
              />
              <span className="underline text-primary">Choose file</span>
            </label>
          </div>
        </div>
      )}

      {selectedFile && (
        <div className="border rounded-md p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <UploadIcon className="h-4 w-4 text-muted-foreground" />
              <span className="font-medium">{selectedFile.name}</span>
              {isUploading && (
                <span className="text-sm text-muted-foreground">
                  Uploading...
                </span>
              )}
            </div>
            {!isUploading && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleRemove}
                type="button"
              >
                Remove
              </Button>
            )}
          </div>
        </div>
      )}

      {forceValidation &&
        field.state.errors &&
        field.state.errors.length > 0 && (
          <p className="text-destructive text-sm">{field.state.errors[0]}</p>
        )}
    </div>
  );
}

// Address structure type
interface AddressData {
  addressLine1: string;
  addressLine2?: string;
  city: string;
  state: string;
  zipCode: string;
}

// Name, Address, Contact structure type
interface NameAddressContactData {
  name: string;
  /** Set for company_contact answers */
  companyName?: string;
  email: string;
  phone: string;
  addressLine1: string;
  addressLine2?: string;
  city: string;
  state: string;
  zipCode: string;
}

// Address validation schema
const addressSchema = z.object({
  addressLine1: z.string().min(1, "Address line 1 is required"),
  addressLine2: z.string().optional(),
  city: z.string().min(1, "City is required"),
  state: z.string().length(2, "State must be 2 characters"),
  zipCode: z
    .string()
    .regex(/^\d{5}(-\d{4})?$/, "Please enter a valid ZIP code"),
});

export type SelectOption = { value: string; label: string };

/** Extract label from last parenthesized part of question text, e.g. "Asset Manager (AMS)" -> "AMS". Supports text after the parens, e.g. "OMT (Ordinance...) - Contact" -> "OMT". */
export function extractBracketLabel(questionText: string): string | null {
  const matches = [...questionText.matchAll(/\(([^)]+)\)/g)];
  return matches.length > 0 ? matches[matches.length - 1][1].trim() : null;
}

/** Drop company when the target question is name/address/contact only; keep it for company_contact. */
function shapeNacCopyForTargetQuestion(
  data: NameAddressContactData,
  includeCompanyName: boolean,
): NameAddressContactData {
  if (includeCompanyName) {
    return { ...data };
  }
  return { ...data, companyName: "" };
}

/** Normalize raw form/API answer to NameAddressContactData shape. */
function normalizeToNameAddressContactData(
  value: unknown,
): NameAddressContactData {
  const o =
    value && typeof value === "object"
      ? (value as Record<string, unknown>)
      : {};
  const a = (k: string) => (o[k] != null ? String(o[k]) : "");
  return {
    name: a("name"),
    companyName: a("companyName") || a("company_name"),
    email: a("email"),
    phone: a("phone"),
    addressLine1: a("addressLine1") || a("address1"),
    addressLine2: a("addressLine2") || a("address2") || undefined,
    city: a("city"),
    state: a("state"),
    zipCode: a("zipCode") || a("zipcode"),
  };
}

/** Internal key: question was filled by "I am the X" or "Y is the X" checkbox; exclude from "copy from" in later questions. */
export const NAC_FILLED_BY_CHECKBOX_KEY = "_filledByCheckbox" as const;

/** True if this name_address_contact value was set by a checkbox (I am the X / Y is the X); such questions are not offered as copy-from for later questions. */
export function isFilledByCheckbox(value: unknown): boolean {
  return !!(
    value &&
    typeof value === "object" &&
    (value as Record<string, unknown>)[NAC_FILLED_BY_CHECKBOX_KEY] === true
  );
}

/** When `QuestionField` is used without question metadata (e.g. date/license dialogs). */
export const DEFAULT_STRICT_NAC_REQUIREMENTS: ContactRequirementValues = {
  contact_name_required: true,
  contact_address_required: true,
  contact_email_required: true,
  contact_phone_required: true,
  contact_company_name_required: true,
  contact_require_company_or_person_name: false,
};

function isNacObjectAllEmpty(o: Record<string, unknown>): boolean {
  const rest = { ...o };
  delete rest[NAC_FILLED_BY_CHECKBOX_KEY];
  const d = normalizeToNameAddressContactData(rest);
  return (
    !d.name.trim() &&
    !(d.companyName ?? "").trim() &&
    !d.email.trim() &&
    !d.phone.trim() &&
    !d.addressLine1.trim() &&
    !(d.addressLine2 ?? "").trim() &&
    !d.city.trim() &&
    !d.state.trim() &&
    !d.zipCode.trim()
  );
}

function isNacValueEmpty(val: unknown): boolean {
  if (val === undefined || val === null) return true;
  if (typeof val === "string") {
    const t = val.trim();
    if (t === "") return true;
    try {
      const p = JSON.parse(t) as Record<string, unknown>;
      return isNacObjectAllEmpty(p);
    } catch {
      return false;
    }
  }
  if (typeof val === "object") {
    return isNacObjectAllEmpty(val as Record<string, unknown>);
  }
  return false;
}

function buildNacShapeZod(
  req: ContactRequirementValues,
  isCompanyContact: boolean,
): z.ZodTypeAny {
  const addrReq = req.contact_address_required;
  const strReq = (required: boolean, label: string) =>
    required ? z.string().min(1, `${label} is required`) : z.string();
  const zipZ = addrReq
    ? z
        .string()
        .min(1, "ZIP code is required")
        .regex(/^\d{5}(-\d{4})?$/, "Please enter a valid ZIP code")
    : z
        .string()
        .refine(
          (s) => s.trim() === "" || /^\d{5}(-\d{4})?$/.test(s),
          "Please enter a valid ZIP code",
        );
  const stateZ = addrReq
    ? z
        .string()
        .min(1, "State is required")
        .length(2, "State must be 2 characters")
    : z
        .string()
        .refine(
          (s) => s.trim() === "" || s.length === 2,
          "State must be 2 characters",
        );
  const phoneZ = req.contact_phone_required
    ? z
        .string()
        .min(1, "Phone number is required")
        .refine(
          (v) => v.replace(/\D/g, "").length === 10,
          "Please enter a valid US phone number",
        )
    : z
        .string()
        .refine(
          (v) => v.trim() === "" || v.replace(/\D/g, "").length === 10,
          "Please enter a valid US phone number",
        );
  const emailZ = req.contact_email_required
    ? z.string().min(1, "Email is required").email("Invalid email format")
    : z
        .string()
        .refine(
          (v) => v.trim() === "" || z.string().email().safeParse(v).success,
          "Invalid email format",
        );

  const baseShape = {
    name: strReq(req.contact_name_required, "Name"),
    email: emailZ,
    phone: phoneZ,
    addressLine1: strReq(addrReq, "Address line 1"),
    addressLine2: z.string().optional(),
    city: strReq(addrReq, "City"),
    state: stateZ,
    zipCode: zipZ,
  };

  if (!isCompanyContact) {
    return z.object({ ...baseShape, companyName: z.string().optional() });
  }

  const companyNameZ = req.contact_company_name_required
    ? z.string().min(1, "Company name is required")
    : z.string();

  return z
    .object({ ...baseShape, companyName: companyNameZ })
    .superRefine((data, ctx) => {
      if (!req.contact_require_company_or_person_name) return;
      if (req.contact_name_required || req.contact_company_name_required)
        return;
      const n = data.name.trim();
      const c = (data.companyName ?? "").trim();
      if (!n && !c) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Enter person name or company name",
          path: ["name"],
        });
      }
    });
}

function nacAnswerZodValidator(
  questionType: "name_address_contact" | "company_contact",
  req: ContactRequirementValues,
  selectFromFixedList: boolean,
): z.ZodTypeAny {
  const needsWholeAnswer = deriveAnswerRequiredForContactQuestion(
    questionType,
    req,
  );
  const isCo = questionType === "company_contact";

  if (selectFromFixedList) {
    if (needsWholeAnswer) {
      return z.union([
        z.string().min(1, "Please select an option"),
        z
          .record(z.string(), z.unknown())
          .refine(
            (o) =>
              o != null &&
              Object.keys(o).length > 0 &&
              !isNacObjectAllEmpty(o as Record<string, unknown>),
            "Please select an option",
          ),
      ]);
    }
    return z
      .union([
        z.string().optional(),
        z.record(z.string(), z.unknown()).optional(),
      ])
      .optional();
  }

  const inner = buildNacShapeZod(req, isCo);
  return z.any().superRefine((val, ctx) => {
    if (isNacValueEmpty(val)) {
      if (needsWholeAnswer) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "This field is required",
        });
      }
      return;
    }
    let raw: Record<string, unknown>;
    if (typeof val === "string") {
      try {
        raw = JSON.parse(val) as Record<string, unknown>;
      } catch {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Invalid name, address, contact format",
        });
        return;
      }
    } else {
      raw = val as Record<string, unknown>;
    }
    const { [NAC_FILLED_BY_CHECKBOX_KEY]: _ignored, ...rest } = raw;
    const normalized = normalizeToNameAddressContactData(rest);
    const r = inner.safeParse(normalized);
    if (!r.success) {
      for (const issue of r.error.issues) {
        ctx.addIssue(issue);
      }
    }
  });
}

/** One previous name_address_contact question option for "ABC is the XYZ" prefilling. */
export interface PreviousNameAddressContactOption {
  questionId: string;
  bracketLabel: string;
  /** When set, the checkbox uses this text instead of "X is the Y". */
  label?: string;
  /** Raw answer (object or JSON) from that question; normalized inside QuestionField. */
  answer: unknown;
}

/** A question that may be offered as a same-registration company copy source. */
export interface ApplicableCompanyQuestion {
  id: string;
  question: string;
  answer_type: string;
  display_order?: number;
  jump_target?: number | null;
  answer_options?: Record<string, number | null> | null;
}

/** Company questions the operator selected that are visible on this registration. */
export function configuredCompanyCopyOptions(args: {
  questionId?: string;
  copyFromQuestionIds?: string[] | null;
  applicableQuestions?: ApplicableCompanyQuestion[] | null;
  answers?: Record<string, unknown> | null;
}): PreviousNameAddressContactOption[] {
  const ids = args.copyFromQuestionIds ?? [];
  const questions = args.applicableQuestions ?? [];
  if (ids.length === 0 || questions.length === 0) return [];
  const visible = getVisibleQuestionsWithJumpLogic(
    questions.map((question) => ({
      ...question,
      display_order: question.display_order ?? 0,
      answer_type: question.answer_type,
    })),
    args.answers ?? {},
  );
  const allowed = new Set(ids);
  return visible
    .filter(
      (question) =>
        question.id !== args.questionId &&
        allowed.has(question.id) &&
        question.answer_type === "company_contact",
    )
    .map((question) => ({
      questionId: question.id,
      bracketLabel: question.question,
      label: question.question,
      answer: args.answers?.[question.id] ?? {},
    }));
}

/** Props for a company question's previous-manager picker and configured copy sources. */
export function companyCopyFieldProps(args: {
  question: {
    id: string;
    answer_type: string;
    suggest_previous_property_managers?: boolean | null;
    copy_from_question_ids?: string[] | null;
    select_from_fixed_list?: boolean | null;
  };
  questions: ApplicableCompanyQuestion[];
  answers: Record<string, unknown>;
  registrantAccountId?: string | null;
  excludeLicenseId?: string | null;
}): Pick<
  QuestionFieldProps,
  | "suggestPreviousPropertyManagers"
  | "copyFromQuestionIds"
  | "applicableQuestions"
  | "applicableAnswers"
  | "registrantAccountId"
  | "excludeLicenseId"
> {
  if (args.question.answer_type !== "company_contact") return {};
  return {
    suggestPreviousPropertyManagers:
      Boolean(args.question.suggest_previous_property_managers) &&
      !args.question.select_from_fixed_list,
    copyFromQuestionIds: args.question.copy_from_question_ids ?? [],
    applicableQuestions: args.questions,
    applicableAnswers: args.answers,
    registrantAccountId: args.registrantAccountId,
    excludeLicenseId: args.excludeLicenseId,
  };
}

interface QuestionFieldProps {
  answerType: LicenseQuestionAnswerType;
  field: any;
  answerOptions?: Array<SelectOption> | null;
  ref?: React.RefObject<HTMLInputElement | null>;
  questionName?: string;
  accountData?: any;
  forceValidation?: boolean;
  userType?: UserType;
  /** Previous name_address_contact questions (with bracket labels) for "ABC is the XYZ" prefilling. */
  previousNameAddressContactOptions?: PreviousNameAddressContactOption[] | null;
  /** Live form values for previous NAC questions (by question id); when user edits a source, we sync this field. */
  previousNacLiveValues?: Record<string, unknown> | null;
  /** Bracket label for current question (e.g. "PM"); used with previousNameAddressContactOptions. */
  currentQuestionBracketLabel?: string | null;
  /** When true and questionId is set, name (and company for company_contact) pick from fixed list; other sub-fields are not editable until a list row is chosen. */
  selectFromFixedList?: boolean;
  /** Question id for fetching fixed-list options (required when selectFromFixedList is true). */
  questionId?: string;
  /** When true, show "I am the X" and "Y is the X" copy-from-registrant options (NAC-style questions). Default true. */
  allowCopyRegistrantInfo?: boolean;
  /** Per-subfield required flags from the question; defaults to all required if omitted. */
  nacRequirements?: ContactRequirementValues;
  /** company_contact only: label person field "Contact", show company name first. */
  useContactLabel?: boolean;
  /** company_contact only: picker of property managers used on the account's other registrations. */
  suggestPreviousPropertyManagers?: boolean;
  /** Account whose other registrations supply the previous-property-manager list. */
  registrantAccountId?: string | null;
  /** License being edited; its saved answer is left out of the previous-manager list. */
  excludeLicenseId?: string | null;
  /** Operator-selected company questions that may be copied when they apply here. */
  copyFromQuestionIds?: string[] | null;
  /** Questions on this registration, before jump-logic filtering. */
  applicableQuestions?: ApplicableCompanyQuestion[] | null;
  /** Live answers for applicableQuestions, keyed by question id. */
  applicableAnswers?: Record<string, unknown> | null;
  /** Rich text displayed above the confirmation action. */
  confirmationText?: string | null;
  /** photo only: allow collecting multiple photos. */
  allowMultiplePhotos?: boolean;
  /** url only: allow more than one URL. */
  allowMultipleUrls?: boolean;
  /** document only: allow more than one file (a sketch or a photo). */
  allowMultipleDocuments?: boolean;
  /** date only: show the value without allowing edits. */
  disabled?: boolean;
}

export function QuestionField({
  answerType,
  field,
  answerOptions,
  ref,
  questionName,
  accountData,
  forceValidation,
  userType,
  previousNameAddressContactOptions,
  previousNacLiveValues,
  currentQuestionBracketLabel,
  selectFromFixedList,
  questionId,
  allowCopyRegistrantInfo = true,
  nacRequirements,
  useContactLabel = false,
  suggestPreviousPropertyManagers = false,
  registrantAccountId,
  excludeLicenseId,
  copyFromQuestionIds,
  applicableQuestions,
  applicableAnswers,
  confirmationText,
  allowMultiplePhotos = false,
  allowMultipleUrls = false,
  allowMultipleDocuments = false,
  disabled = false,
}: QuestionFieldProps) {
  const handleChange = (value: any) => field.handleChange(value);
  const iAmTheCheckboxFallbackId = useId();
  /** Unique per QuestionField so label htmlFor matches this checkbox when multiple NAC questions exist. */
  const iAmTheCheckboxId = questionId
    ? `i-am-the-contact-${questionId}`
    : `i-am-the-contact${iAmTheCheckboxFallbackId}`;

  // Validation state for individual fields - moved to top level to avoid hooks violation
  const [focusedFields, setFocusedFields] = useState<Set<string>>(new Set());
  // Which "ABC is the XYZ" option is selected (mutually exclusive with "I am the")
  const [selectedPreviousNacId, setSelectedPreviousNacId] = useState<
    string | null
  >(null);

  // Address lookup functionality - hooks are now properly called at component top level
  const [placesAutoCompleteInput, setPlacesAutoCompleteInput] = useState("");
  const { suggestions } = usePlacesAutocomplete(placesAutoCompleteInput, {
    includedRegionCodes: ["us"],
    includedPrimaryTypes: ["street_address"],
  });

  // State for name_address_contact collapse functionality
  const [isCollapsed, setIsCollapsed] = useState(false);
  // When using fixed list, which NAC option was selected (so we show other fields read-only)
  const [selectedFixedListNacId, setSelectedFixedListNacId] = useState<
    string | null
  >(null);

  const useFixedList =
    isNacStyleAnswerType(answerType) && !!selectFromFixedList && !!questionId;
  const { data: questionOptionsData } = useQuery({
    ...getQuestionOptionsOptions({
      query: { question_id: questionId ?? undefined },
    }),
    enabled: useFixedList,
  });
  const fixedListNacOptions = useMemo(() => {
    if (!questionOptionsData?.question_options) return [];
    return questionOptionsData.question_options
      .filter((o) => {
        if (o.deleted_at || !o.party) return false;
        const accountType = (o.party as { account_type?: string | null })
          .account_type;
        return accountType !== "SECONDARY";
      })
      .map((o) => ({
        nacId: o.party_id,
        name: o.party?.name ?? "",
        companyName: o.party?.company_name ?? "",
        email: o.party?.email ?? "",
        phone: o.party?.phone ?? "",
        address1: o.party?.address1 ?? "",
        address2: o.party?.address2 ?? "",
        city: o.party?.city ?? "",
        state: o.party?.state ?? "",
        zipcode: o.party?.zipcode ?? "",
      }));
  }, [questionOptionsData]);
  const fixedListParties = useMemo(
    () =>
      fixedListNacOptions.map((option) => ({
        value: option.nacId,
        companyName: option.companyName,
        contactName: option.name,
        email: option.email,
      })),
    [fixedListNacOptions],
  );

  const needsAccountForPreviousManagers =
    userType === UserType.OPERATOR || userType === UserType.GOVERNMENT;
  const loadPreviousManagers =
    answerType === "company_contact" &&
    suggestPreviousPropertyManagers &&
    !selectFromFixedList &&
    (!needsAccountForPreviousManagers || !!registrantAccountId);
  const previousManagersQuery = {
    query: {
      registrant_account_id: registrantAccountId || undefined,
      exclude_license_id: excludeLicenseId || undefined,
    },
  };
  const previousManagersOptions = (
    loadPreviousManagers
      ? getPreviousPropertyManagersOptions(previousManagersQuery)
      : {
          queryKey: ["previous-property-managers", "idle"],
          queryFn: async () => ({ property_managers: [] }),
        }
  ) as ReturnType<typeof getPreviousPropertyManagersOptions>;
  const { data: previousManagersData } = useQuery({
    ...previousManagersOptions,
    enabled: loadPreviousManagers,
  });
  const previousManagers = previousManagersData?.property_managers ?? [];

  const configuredCompanyCopies = useMemo(
    () =>
      answerType === "company_contact"
        ? configuredCompanyCopyOptions({
            questionId,
            copyFromQuestionIds,
            applicableQuestions,
            answers: applicableAnswers,
          })
        : [],
    [
      answerType,
      applicableAnswers,
      applicableQuestions,
      copyFromQuestionIds,
      questionId,
    ],
  );
  const nacCopyOptions =
    answerType === "company_contact"
      ? configuredCompanyCopies
      : (previousNameAddressContactOptions ?? []);
  const nacLiveValues =
    answerType === "company_contact"
      ? (applicableAnswers ?? null)
      : previousNacLiveValues;

  // When user has selected "ABC is the XYZ", keep this field in sync when they edit that source (must be at top level for hooks rule)
  // biome-ignore lint/correctness/useExhaustiveDependencies: only sync when source or selection changes; adding field.state.value/handleChange would cause loops or unnecessary runs
  useEffect(() => {
    if (
      !isNacStyleAnswerType(answerType) ||
      !selectedPreviousNacId ||
      !nacLiveValues
    )
      return;
    const raw = nacLiveValues[selectedPreviousNacId];
    if (raw === undefined) return;
    const parsed: Record<string, unknown> =
      typeof raw === "string"
        ? (() => {
            try {
              return (JSON.parse(raw) as Record<string, unknown>) ?? {};
            } catch {
              return {};
            }
          })()
        : ((raw as Record<string, unknown>) ?? {});
    const normalized = normalizeToNameAddressContactData(parsed);
    const shaped = shapeNacCopyForTargetQuestion(
      normalized,
      answerType === "company_contact",
    );
    const current = field.state.value as NameAddressContactData | undefined;
    const currentNorm =
      current != null
        ? shapeNacCopyForTargetQuestion(
            normalizeToNameAddressContactData(current),
            answerType === "company_contact",
          )
        : null;
    if (
      currentNorm == null ||
      JSON.stringify(shaped) !== JSON.stringify(currentNorm)
    ) {
      handleChange({
        ...shaped,
        [NAC_FILLED_BY_CHECKBOX_KEY]: true,
      } as NameAddressContactData & Record<string, unknown>);
    }
  }, [answerType, nacLiveValues, selectedPreviousNacId]);

  switch (answerType) {
    case "confirmation": {
      const confirmed = field.state.value === true;
      return (
        <div className="space-y-3">
          {confirmationText ? (
            <div className="rounded-md border bg-muted/50">
              <RichTextHtml html={confirmationText} className="p-4" />
            </div>
          ) : null}
          <Button
            type="button"
            variant={confirmed ? "secondary" : "default"}
            disabled={confirmed}
            onClick={() => handleChange(true)}
          >
            {confirmed ? "Confirmed" : "Confirm"}
          </Button>
        </div>
      );
    }
    // Live Link is answered as a single-line text value; the link is rendered
    // later (in the license/property details) from the operator's URL template.
    case "live_link":
    case "text": {
      const hasErrors = field.state.errors && field.state.errors.length > 0;
      const shouldShowErrors =
        hasErrors && (forceValidation || focusedFields.has("text"));
      return (
        <div className="space-y-1">
          <Input
            ref={ref}
            type="text"
            value={(field.state.value as string) ?? ""}
            onChange={(e) => handleChange(e.target.value)}
            onBlur={() => {
              field.handleBlur();
              setFocusedFields((prev) => new Set(prev).add("text"));
            }}
            aria-invalid={shouldShowErrors}
          />
          {shouldShowErrors && (
            <p className="text-destructive text-sm">{field.state.errors[0]}</p>
          )}
        </div>
      );
    }
    case "number": {
      const hasErrors = field.state.errors && field.state.errors.length > 0;
      const shouldShowErrors =
        hasErrors && (forceValidation || focusedFields.has("number"));
      const rawValue = field.state.value as string | number | undefined;
      const inputValue =
        typeof rawValue === "number"
          ? Number.isFinite(rawValue)
            ? rawValue
            : ""
          : (rawValue ?? "");

      return (
        <div className="space-y-1">
          <Input
            ref={ref}
            type="number"
            value={inputValue}
            onInput={(e: React.ChangeEvent<HTMLInputElement>) => {
              const { value, valueAsNumber } = e.target;
              if (value === "") {
                handleChange("");
              } else if (!Number.isNaN(valueAsNumber)) {
                handleChange(valueAsNumber);
              } else {
                handleChange(value);
              }
            }}
            onBlur={() => {
              field.handleBlur();
              setFocusedFields((prev) => new Set(prev).add("number"));
            }}
            aria-invalid={shouldShowErrors}
          />
          {shouldShowErrors && (
            <p className="text-destructive text-sm">{field.state.errors[0]}</p>
          )}
        </div>
      );
    }
    case "date": {
      const hasErrors = field.state.errors && field.state.errors.length > 0;
      const shouldShowErrors =
        hasErrors && (forceValidation || focusedFields.has("date"));
      return (
        <div className="space-y-1">
          <DatePicker
            value={
              field.state.value
                ? parse(field.state.value as string, "yyyy-MM-dd", new Date())
                : null
            }
            onChange={(date) =>
              handleChange(date ? format(date, "yyyy-MM-dd") : null)
            }
            onBlur={() => {
              field.handleBlur();
              setFocusedFields((prev) => new Set(prev).add("date"));
            }}
            aria-invalid={shouldShowErrors}
            disabled={disabled}
            clearable={!disabled}
          />
          {shouldShowErrors && (
            <p className="text-destructive text-sm">{field.state.errors[0]}</p>
          )}
        </div>
      );
    }
    case "boolean": {
      const raw = field.state.value;
      const selected =
        raw === true || raw === "true"
          ? "true"
          : raw === false || raw === "false"
            ? "false"
            : "";
      const hasErrors = field.state.errors && field.state.errors.length > 0;
      const shouldShowErrors =
        hasErrors && (forceValidation || focusedFields.has("boolean"));
      const yesId = `boolean-yes-${questionId ?? questionName ?? iAmTheCheckboxFallbackId}`;
      const noId = `boolean-no-${questionId ?? questionName ?? iAmTheCheckboxFallbackId}`;
      return (
        <div className="space-y-1">
          <RadioGroup
            value={selected}
            onValueChange={(next) => {
              handleChange(next === "true");
              setFocusedFields((prev) => new Set(prev).add("boolean"));
            }}
            onBlur={field.handleBlur}
            className="flex flex-row flex-wrap gap-4"
            aria-invalid={shouldShowErrors}
          >
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="true" id={yesId} />
              <Label htmlFor={yesId} className="text-sm font-normal">
                Yes
              </Label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="false" id={noId} />
              <Label htmlFor={noId} className="text-sm font-normal">
                No
              </Label>
            </div>
          </RadioGroup>
          {shouldShowErrors && (
            <p className="text-destructive text-sm">{field.state.errors[0]}</p>
          )}
        </div>
      );
    }
    case "single_select": {
      return (
        <Select
          options={answerOptions ?? []}
          value={
            (field.state.value as string) ? [field.state.value as string] : []
          }
          onValueChange={(values) => handleChange(values[0] || "")}
          placeholder="Select an option"
          searchPlaceholder="Search options..."
          emptyMessage="No options found."
        />
      );
    }
    case "multi_select": {
      const selectedValues = (field.state.value as string[]) ?? [];
      return (
        <div className="flex flex-col gap-2">
          <Select
            value={selectedValues}
            onValueChange={(value) => handleChange(value)}
            options={answerOptions ?? []}
            multiple
            showBadges
            placeholder="Select options"
            searchPlaceholder="Search options..."
            emptyMessage="No options found."
          />
        </div>
      );
    }
    case "email": {
      return (
        <Input
          type="email"
          value={(field.state.value as string) ?? ""}
          onChange={(e) => handleChange(e.target.value)}
          placeholder="Email address"
          ref={ref}
        />
      );
    }
    case "phone_number": {
      return (
        <Input
          type="tel"
          value={formatPhoneNumber((field.state.value as string) ?? "")}
          onChange={(e) => handleChange(e.target.value)}
          placeholder="(XXX) XXX-XXXX"
          ref={ref}
        />
      );
    }
    case "address": {
      const addressValue = field.state.value as string | null | undefined;
      let addressData: AddressData = {
        addressLine1: "",
        addressLine2: "",
        city: "",
        state: "",
        zipCode: "",
      };

      // Try to parse the JSON string value
      if (addressValue && typeof addressValue === "string") {
        try {
          const parsed = JSON.parse(addressValue);
          if (parsed && typeof parsed === "object") {
            addressData = {
              addressLine1: parsed.addressLine1 || "",
              addressLine2: parsed.addressLine2 || "",
              city: parsed.city || "",
              state: parsed.state || "",
              zipCode: parsed.zipCode || "",
            };
          }
        } catch {
          // If parsing fails, use empty address data
        }
      }

      const handleAddressChange = (
        fieldName: keyof AddressData,
        value: string,
      ) => {
        const newAddressData = { ...addressData, [fieldName]: value };
        handleChange(JSON.stringify(newAddressData));
      };

      const handleSuggestionClick = async (
        suggestion: google.maps.places.AutocompleteSuggestion,
      ) => {
        const place = suggestion.placePrediction?.toPlace();
        if (!place) {
          return;
        }
        await place.fetchFields({
          fields: ["addressComponents"],
        });

        if (!place?.addressComponents) {
          return;
        }

        const { address1, address2, city, state, zipcode } =
          parseGoogleAddressComponents(place.addressComponents);

        const newAddressData = {
          ...addressData,
          addressLine1: address1,
          addressLine2: address2,
          city: city,
          state: state,
          zipCode: zipcode,
        };
        handleChange(JSON.stringify(newAddressData));
      };

      return (
        <div
          className="space-y-3 rounded-md border p-4 bg-background"
          ref={ref}
        >
          <div>
            <Label htmlFor="address-line-1" className="text-sm font-medium">
              Address Line 1 *
            </Label>
            <SuggestionList
              closeOnBlur
              suggestions={suggestions}
              renderSuggestion={(suggestion) => (
                <span>{suggestion.placePrediction?.text?.text}</span>
              )}
              getSuggestionKey={(suggestion) =>
                suggestion.placePrediction?.placeId ?? ""
              }
              onSuggestionSelect={handleSuggestionClick}
            >
              <Input
                id="address-line-1"
                type="text"
                value={addressData.addressLine1}
                onChange={(e) => {
                  handleAddressChange("addressLine1", e.target.value);
                  setPlacesAutoCompleteInput(e.target.value);
                }}
                placeholder="Enter your street address"
                className="mt-1"
              />
            </SuggestionList>
          </div>

          <div>
            <Label htmlFor="address-line-2" className="text-sm font-medium">
              Address Line 2
            </Label>
            <Input
              id="address-line-2"
              type="text"
              value={addressData.addressLine2 || ""}
              onChange={(e) =>
                handleAddressChange("addressLine2", e.target.value)
              }
              placeholder="Apartment, suite, etc. (optional)"
              className="mt-1"
            />
          </div>

          <div className="grid md:grid-cols-3 gap-4 items-start">
            <div>
              <Label htmlFor="city" className="text-sm font-medium">
                City *
              </Label>
              <Input
                id="city"
                type="text"
                value={addressData.city}
                onChange={(e) => handleAddressChange("city", e.target.value)}
                placeholder="Enter your city"
                className="mt-1"
              />
            </div>

            <div>
              <Label htmlFor="state" className="text-sm font-medium">
                State *
              </Label>
              <Input
                id="state"
                type="text"
                value={addressData.state}
                onChange={(e) => handleAddressChange("state", e.target.value)}
                placeholder="XX"
                maxLength={2}
                className="mt-1"
              />
            </div>

            <div>
              <Label htmlFor="zip-code" className="text-sm font-medium">
                ZIP Code *
              </Label>
              <Input
                id="zip-code"
                type="text"
                value={addressData.zipCode}
                onChange={(e) => handleAddressChange("zipCode", e.target.value)}
                placeholder="XXXXX or XXXXX-XXXX"
                className="mt-1"
              />
            </div>
          </div>
        </div>
      );
    }
    case "name_address_contact":
    case "company_contact": {
      const includeCompanyName = answerType === "company_contact";
      const nacReq = nacRequirements ?? DEFAULT_STRICT_NAC_REQUIREMENTS;
      const addrReq = nacReq.contact_address_required;
      const eitherOrOnly =
        includeCompanyName &&
        nacReq.contact_require_company_or_person_name &&
        !nacReq.contact_name_required &&
        !nacReq.contact_company_name_required;
      const useCoLabel = Boolean(useContactLabel && includeCompanyName);
      const personNameLabelText = useCoLabel ? "Contact" : "Name";
      const selectFromListPlaceholder = "Select from the list above";
      const reqMark = (flag: boolean) => (flag ? " *" : "");
      const contactValue = field.state.value as string | null | undefined;
      let contactData: NameAddressContactData = {
        name: "",
        companyName: "",
        email: "",
        phone: "",
        addressLine1: "",
        addressLine2: "",
        city: "",
        state: "",
        zipCode: "",
      };

      // Handle the JSON string format
      if (contactValue && typeof contactValue === "string") {
        try {
          const parsed = JSON.parse(contactValue);
          contactData = {
            name: parsed.name || "",
            companyName: parsed.companyName || parsed.company_name || "",
            email: parsed.email || "",
            phone: parsed.phone || "",
            addressLine1: parsed.addressLine1 || parsed.address1 || "",
            addressLine2: parsed.addressLine2 || parsed.address2 || "",
            city: parsed.city || "",
            state: parsed.state || "",
            zipCode: parsed.zipCode || parsed.zipcode || "",
          };
        } catch (error) {
          // If parsing fails, use default empty values
        }
      } else if (contactValue && typeof contactValue === "object") {
        // Handle object format (direct object instead of JSON string)
        const o = contactValue as Record<string, unknown>;
        contactData = {
          name: (o.name as string) || "",
          companyName:
            (o.companyName as string) || (o.company_name as string) || "",
          email: (o.email as string) || "",
          phone: (o.phone as string) || "",
          addressLine1:
            (o.addressLine1 as string) || (o.address1 as string) || "",
          addressLine2:
            (o.addressLine2 as string) || (o.address2 as string) || "",
          city: (o.city as string) || "",
          state: (o.state as string) || "",
          zipCode: (o.zipCode as string) || (o.zipcode as string) || "",
        };
      }

      // Helper function to determine if error should be shown
      const shouldShowError = (fieldName: string) => {
        return focusedFields.has(fieldName) || forceValidation;
      };

      // When using fixed list, do not validate sub-fields; only "something selected" is validated at form level
      const getFieldError = (
        fieldName: keyof NameAddressContactData,
        value: string,
      ) => {
        if (useFixedList) return null;
        if (!shouldShowError(fieldName)) return null;

        switch (fieldName) {
          case "name":
            if (nacReq.contact_name_required && value.trim() === "")
              return useCoLabel ? "Contact is required" : "Name is required";
            if (
              eitherOrOnly &&
              !value.trim() &&
              !(contactData.companyName ?? "").trim()
            ) {
              return "Enter person name or company name";
            }
            return null;
          case "companyName":
            if (!includeCompanyName) return null;
            if (nacReq.contact_company_name_required && value.trim() === "")
              return "Company name is required";
            if (eitherOrOnly && !value.trim() && !contactData.name.trim()) {
              return "Enter person name or company name";
            }
            return null;
          case "email":
            if (nacReq.contact_email_required && value.trim() === "")
              return "Email is required";
            if (
              value.trim() !== "" &&
              !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)
            )
              return "Invalid email format";
            return null;
          case "phone":
            if (nacReq.contact_phone_required && value.trim() === "")
              return "Phone number is required";
            if (value.trim() !== "" && value.replace(/\D/g, "").length !== 10) {
              return "Please enter a valid US phone number";
            }
            return null;
          case "addressLine1":
            if (addrReq && value.trim() === "")
              return "Address line 1 is required";
            return null;
          case "city":
            if (addrReq && value.trim() === "") return "City is required";
            return null;
          case "state":
            if (addrReq && value.trim() === "") return "State is required";
            if (value.trim() !== "" && value.length !== 2)
              return "State must be 2 characters";
            return null;
          case "zipCode":
            if (addrReq && value.trim() === "") return "ZIP code is required";
            if (value.trim() !== "" && !/^\d{5}(-\d{4})?$/.test(value))
              return "Please enter a valid ZIP code";
            return null;
          default:
            return null;
        }
      };

      const handleContactChange = (
        fieldName: keyof NameAddressContactData,
        value: string,
      ) => {
        const newContactData = { ...contactData, [fieldName]: value };
        // Store as object directly (not JSON string) to match import format
        handleChange(newContactData);
      };

      const applyFixedListPartyOption = (
        opt: (typeof fixedListNacOptions)[number],
      ) => {
        handleChange({
          partyId: opt.nacId,
          name: opt.name,
          companyName: opt.companyName ?? "",
          email: opt.email,
          phone: opt.phone,
          addressLine1: opt.address1,
          addressLine2: opt.address2,
          city: opt.city,
          state: opt.state,
          zipCode: opt.zipcode,
        });
        setSelectedFixedListNacId(opt.nacId);
      };

      // Saved answers keep the party's details but not its id, so an existing
      // answer is matched back to its row by name and company.
      const fixedListSelectedNacId =
        selectedFixedListNacId ??
        fixedListNacOptions.find(
          (opt) =>
            opt.name === contactData.name &&
            (opt.companyName ?? "") === (contactData.companyName ?? ""),
        )?.nacId ??
        null;

      const selectFixedListParty = (partyId: string | undefined) => {
        const opt = fixedListNacOptions.find((o) => o.nacId === partyId);
        if (opt) applyFixedListPartyOption(opt);
      };

      const handleIAmTheChange = (checked: boolean) => {
        if (checked && accountData) {
          setSelectedPreviousNacId(null);
          setSelectedFixedListNacId(null);
          const registrantCompany = includeCompanyName
            ? (getRegistrantDisplayCompanyName(accountData) ?? "")
            : "";
          // Use display helpers so secondary accounts get primary company + mailing address
          // while keeping the logged-in user's name and raw email/phone.
          const accountContactData: NameAddressContactData = {
            name: getRegistrantDisplayName(accountData) ?? "",
            companyName: registrantCompany,
            email: accountData.email || "",
            phone: accountData.phone || "",
            addressLine1: getRegistrantDisplayAddress1(accountData) ?? "",
            addressLine2: getRegistrantDisplayAddress2(accountData) ?? "",
            city: getRegistrantDisplayCity(accountData) ?? "",
            state: getRegistrantDisplayState(accountData) ?? "",
            zipCode: getRegistrantDisplayZipcode(accountData) ?? "",
          };
          handleChange({
            ...accountContactData,
            [NAC_FILLED_BY_CHECKBOX_KEY]: true,
          } as NameAddressContactData & Record<string, unknown>);
        }
        if (!checked) setSelectedPreviousNacId(null);
        setIsCollapsed(checked);
      };

      const handlePreviousNacOptionChange = (
        option: PreviousNameAddressContactOption,
        checked: boolean,
      ) => {
        if (checked) {
          setSelectedPreviousNacId(option.questionId);
          setSelectedFixedListNacId(null);
          let raw = option.answer;
          if (typeof raw === "string") {
            try {
              raw = JSON.parse(raw) as Record<string, unknown>;
            } catch {
              raw = {};
            }
          }
          const data = shapeNacCopyForTargetQuestion(
            normalizeToNameAddressContactData(raw),
            includeCompanyName,
          );
          handleChange({
            ...data,
            [NAC_FILLED_BY_CHECKBOX_KEY]: true,
          } as NameAddressContactData & Record<string, unknown>);
          setIsCollapsed(true);
        } else {
          setSelectedPreviousNacId(null);
          setIsCollapsed(false);
        }
      };

      const showAutomaticCopies =
        allowCopyRegistrantInfo &&
        answerType !== "company_contact" &&
        !!currentQuestionBracketLabel &&
        nacCopyOptions.length > 0;
      const showConfiguredCopies =
        answerType === "company_contact" && nacCopyOptions.length > 0;
      const copyOptionsToShow = showConfiguredCopies
        ? nacCopyOptions
        : showAutomaticCopies
          ? nacCopyOptions
          : [];
      const displayLabel =
        currentQuestionBracketLabel ?? questionName ?? "contact";
      const showPreviousContacts =
        suggestPreviousPropertyManagers &&
        !selectFromFixedList &&
        previousManagers.length > 0;
      const applyPreviousContact = (manager: PreviousContactSuggestion) => {
        setSelectedPreviousNacId(null);
        setSelectedFixedListNacId(null);
        setIsCollapsed(false);
        handleChange({
          name: manager.name ?? "",
          companyName: manager.company_name ?? "",
          email: manager.email ?? "",
          phone: manager.phone ?? "",
          addressLine1: manager.address1 ?? "",
          addressLine2: manager.address2 ?? "",
          city: manager.city ?? "",
          state: manager.state ?? "",
          zipCode: manager.zipcode ?? "",
        });
      };
      const previousContactInput = (
        input: React.ReactElement<{
          onKeyDown?: (event: React.KeyboardEvent) => void;
          onFocus?: (event: React.FocusEvent) => void;
          onBlur?: (event: React.FocusEvent) => void;
        }>,
        labelBy: "company" | "contact",
        query: string,
      ) =>
        showPreviousContacts ? (
          <PreviousContactAutocomplete
            contacts={previousManagers}
            labelBy={labelBy}
            query={query}
            onSelect={applyPreviousContact}
          >
            {input}
          </PreviousContactAutocomplete>
        ) : (
          input
        );
      const isIAmTheChecked = isCollapsed && selectedPreviousNacId === null;

      const handleSuggestionClick = async (
        suggestion: google.maps.places.AutocompleteSuggestion,
      ) => {
        const place = suggestion.placePrediction?.toPlace();
        if (!place) {
          return;
        }
        await place.fetchFields({
          fields: ["addressComponents"],
        });

        if (!place?.addressComponents) {
          return;
        }

        const { address1, address2, city, state, zipcode } =
          parseGoogleAddressComponents(place.addressComponents);

        const newContactData: NameAddressContactData = {
          ...contactData,
          addressLine1: address1,
          addressLine2: address2,
          city: city,
          state: state,
          zipCode: zipcode,
        };
        // Store as object directly (not JSON string) to match import format
        handleChange(newContactData);
      };

      const renderNacNameFieldBlock = () => (
        <div>
          <Label htmlFor="name" className="text-sm font-medium">
            {personNameLabelText}
            {reqMark(nacReq.contact_name_required || eitherOrOnly)}
          </Label>
          {useFixedList && fixedListParties.length > 0 ? (
            <PartySelect
              triggerId="name"
              parties={fixedListParties}
              labelBy="contact"
              value={fixedListSelectedNacId ? [fixedListSelectedNacId] : []}
              onValueChange={(next) => selectFixedListParty(next[0])}
              triggerLabel={contactData.name}
              placeholder={useCoLabel ? "Select contact…" : "Select name…"}
              searchPlaceholder={
                useCoLabel ? "Search contacts…" : "Search names…"
              }
              emptyMessage={useCoLabel ? "No contact found." : "No name found."}
              className={cn(
                "mt-1 font-normal",
                getFieldError("name", contactData.name) && "border-red-500",
              )}
              contentClassName="w-[var(--radix-popover-trigger-width)]"
            />
          ) : (
            previousContactInput(
              <Input
                id="name"
                type="text"
                value={contactData.name}
                onChange={(e) => handleContactChange("name", e.target.value)}
                onFocus={() =>
                  setFocusedFields((prev) => new Set(prev).add("name"))
                }
                placeholder={
                  useCoLabel ? "Enter contact name" : "Enter full name"
                }
                className={cn(
                  "mt-1",
                  getFieldError("name", contactData.name) && "border-red-500",
                )}
              />,
              "contact",
              contactData.name,
            )
          )}
          {getFieldError("name", contactData.name) && (
            <p className="text-sm text-red-500 mt-1">
              {getFieldError("name", contactData.name)}
            </p>
          )}
        </div>
      );

      const renderNacCompanyFieldBlock = () =>
        !includeCompanyName ? null : (
          <div>
            <Label htmlFor="company-name" className="text-sm font-medium">
              Company name
              {reqMark(nacReq.contact_company_name_required || eitherOrOnly)}
            </Label>
            {useFixedList && fixedListParties.length > 0 ? (
              <PartySelect
                triggerId="company-name"
                parties={fixedListParties}
                labelBy="company"
                value={fixedListSelectedNacId ? [fixedListSelectedNacId] : []}
                onValueChange={(next) => selectFixedListParty(next[0])}
                triggerLabel={contactData.companyName ?? ""}
                placeholder={
                  useCoLabel ? "Select company…" : "Select company name…"
                }
                searchPlaceholder="Search companies…"
                emptyMessage="No company found."
                className={cn(
                  "mt-1 font-normal",
                  getFieldError("companyName", contactData.companyName ?? "") &&
                    "border-red-500",
                )}
                contentClassName="w-[var(--radix-popover-trigger-width)]"
              />
            ) : (
              previousContactInput(
                <Input
                  id="company-name"
                  type="text"
                  value={contactData.companyName ?? ""}
                  onChange={(e) =>
                    handleContactChange("companyName", e.target.value)
                  }
                  onFocus={() =>
                    setFocusedFields((prev) => new Set(prev).add("companyName"))
                  }
                  placeholder="Enter company name"
                  className={cn(
                    "mt-1",
                    getFieldError(
                      "companyName",
                      contactData.companyName ?? "",
                    ) && "border-red-500",
                  )}
                />,
                "company",
                contactData.companyName ?? "",
              )
            )}
            {getFieldError("companyName", contactData.companyName ?? "") && (
              <p className="text-sm text-red-500 mt-1">
                {getFieldError("companyName", contactData.companyName ?? "")}
              </p>
            )}
          </div>
        );

      return (
        <div
          className="space-y-3 rounded-md border p-4 bg-background"
          ref={ref}
        >
          {(allowCopyRegistrantInfo || copyOptionsToShow.length > 0) && (
            <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
              {allowCopyRegistrantInfo && (
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id={iAmTheCheckboxId}
                    checked={isIAmTheChecked}
                    onCheckedChange={(checked) => {
                      handleIAmTheChange(checked as boolean);
                    }}
                  />
                  <Label
                    htmlFor={iAmTheCheckboxId}
                    className="text-sm font-medium"
                  >
                    {userType === UserType.OPERATOR
                      ? `The registrant is the ${displayLabel}`
                      : `I am the ${displayLabel}`}
                  </Label>
                </div>
              )}
              {copyOptionsToShow.map((option) => (
                <div
                  key={option.questionId}
                  className="flex items-center space-x-2"
                >
                  <Checkbox
                    id={`prev-nac-${option.questionId}`}
                    checked={selectedPreviousNacId === option.questionId}
                    onCheckedChange={(checked) => {
                      if (checked) {
                        handlePreviousNacOptionChange(option, true);
                      } else {
                        handlePreviousNacOptionChange(option, false);
                      }
                    }}
                  />
                  <Label
                    htmlFor={`prev-nac-${option.questionId}`}
                    className="text-sm font-medium"
                  >
                    {option.label ??
                      `${option.bracketLabel} is the ${currentQuestionBracketLabel ?? displayLabel}`}
                  </Label>
                </div>
              ))}
            </div>
          )}

          {!(
            isCollapsed &&
            (allowCopyRegistrantInfo || selectedPreviousNacId !== null)
          ) && (
            <>
              {includeCompanyName ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {useCoLabel ? (
                    <>
                      {renderNacCompanyFieldBlock()}
                      {renderNacNameFieldBlock()}
                    </>
                  ) : (
                    <>
                      {renderNacNameFieldBlock()}
                      {renderNacCompanyFieldBlock()}
                    </>
                  )}
                </div>
              ) : (
                renderNacNameFieldBlock()
              )}

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="email" className="text-sm font-medium">
                    Email{reqMark(nacReq.contact_email_required)}
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    value={contactData.email}
                    onChange={(e) =>
                      !useFixedList &&
                      handleContactChange("email", e.target.value)
                    }
                    disabled={useFixedList}
                    onFocus={() =>
                      setFocusedFields((prev) => new Set(prev).add("email"))
                    }
                    placeholder={
                      useFixedList
                        ? selectFromListPlaceholder
                        : "Enter email address"
                    }
                    className={cn(
                      "mt-1",
                      getFieldError("email", contactData.email) &&
                        "border-red-500",
                    )}
                  />
                  {getFieldError("email", contactData.email) && (
                    <p className="text-sm text-red-500 mt-1">
                      {getFieldError("email", contactData.email)}
                    </p>
                  )}
                </div>

                <div>
                  <Label htmlFor="phone" className="text-sm font-medium">
                    Phone{reqMark(nacReq.contact_phone_required)}
                  </Label>
                  <Input
                    id="phone"
                    type="tel"
                    value={formatPhoneNumber(contactData.phone)}
                    onChange={(e) =>
                      !useFixedList &&
                      handleContactChange("phone", e.target.value)
                    }
                    disabled={useFixedList}
                    onFocus={() =>
                      setFocusedFields((prev) => new Set(prev).add("phone"))
                    }
                    placeholder={
                      useFixedList
                        ? selectFromListPlaceholder
                        : "(XXX) XXX-XXXX"
                    }
                    className={cn(
                      "mt-1",
                      getFieldError("phone", contactData.phone) &&
                        "border-red-500",
                    )}
                  />
                  {getFieldError("phone", contactData.phone) && (
                    <p className="text-sm text-red-500 mt-1">
                      {getFieldError("phone", contactData.phone)}
                    </p>
                  )}
                </div>
              </div>

              <div>
                <Label
                  htmlFor="contact-address-line-1"
                  className="text-sm font-medium"
                >
                  Address Line 1{reqMark(addrReq)}
                </Label>
                {useFixedList ? (
                  <Input
                    id="contact-address-line-1"
                    type="text"
                    value={contactData.addressLine1}
                    disabled
                    placeholder={selectFromListPlaceholder}
                    className={cn(
                      "mt-1",
                      getFieldError("addressLine1", contactData.addressLine1) &&
                        "border-red-500",
                    )}
                  />
                ) : (
                  <SuggestionList
                    closeOnBlur
                    suggestions={suggestions}
                    renderSuggestion={(suggestion) => (
                      <span>{suggestion.placePrediction?.text?.text}</span>
                    )}
                    getSuggestionKey={(suggestion) =>
                      suggestion.placePrediction?.placeId ?? ""
                    }
                    onSuggestionSelect={handleSuggestionClick}
                  >
                    <Input
                      id="contact-address-line-1"
                      type="text"
                      value={contactData.addressLine1}
                      onChange={(e) => {
                        handleContactChange("addressLine1", e.target.value);
                        setPlacesAutoCompleteInput(e.target.value);
                      }}
                      onFocus={() =>
                        setFocusedFields((prev) =>
                          new Set(prev).add("addressLine1"),
                        )
                      }
                      placeholder="Enter your street address"
                      className={cn(
                        "mt-1",
                        getFieldError(
                          "addressLine1",
                          contactData.addressLine1,
                        ) && "border-red-500",
                      )}
                    />
                  </SuggestionList>
                )}
                {getFieldError("addressLine1", contactData.addressLine1) && (
                  <p className="text-sm text-red-500 mt-1">
                    {getFieldError("addressLine1", contactData.addressLine1)}
                  </p>
                )}
              </div>

              <div>
                <Label
                  htmlFor="contact-address-line-2"
                  className="text-sm font-medium"
                >
                  Address Line 2
                </Label>
                <Input
                  id="contact-address-line-2"
                  type="text"
                  value={contactData.addressLine2 || ""}
                  onChange={(e) =>
                    !useFixedList &&
                    handleContactChange("addressLine2", e.target.value)
                  }
                  disabled={useFixedList}
                  placeholder={
                    useFixedList
                      ? selectFromListPlaceholder
                      : "Apartment, suite, etc. (optional)"
                  }
                  className="mt-1"
                />
              </div>

              <div className="grid md:grid-cols-3 gap-4 items-start">
                <div>
                  <Label htmlFor="contact-city" className="text-sm font-medium">
                    City{reqMark(addrReq)}
                  </Label>
                  <Input
                    id="contact-city"
                    type="text"
                    value={contactData.city}
                    onChange={(e) =>
                      !useFixedList &&
                      handleContactChange("city", e.target.value)
                    }
                    disabled={useFixedList}
                    onFocus={() =>
                      setFocusedFields((prev) => new Set(prev).add("city"))
                    }
                    placeholder={
                      useFixedList
                        ? selectFromListPlaceholder
                        : "Enter your city"
                    }
                    className={cn(
                      "mt-1",
                      getFieldError("city", contactData.city) &&
                        "border-red-500",
                    )}
                  />
                  {getFieldError("city", contactData.city) && (
                    <p className="text-sm text-red-500 mt-1">
                      {getFieldError("city", contactData.city)}
                    </p>
                  )}
                </div>

                <div>
                  <Label
                    htmlFor="contact-state"
                    className="text-sm font-medium"
                  >
                    State{reqMark(addrReq)}
                  </Label>
                  <Input
                    id="contact-state"
                    type="text"
                    value={contactData.state}
                    onChange={(e) =>
                      !useFixedList &&
                      handleContactChange("state", e.target.value)
                    }
                    disabled={useFixedList}
                    onFocus={() =>
                      setFocusedFields((prev) => new Set(prev).add("state"))
                    }
                    placeholder={
                      useFixedList ? selectFromListPlaceholder : "XX"
                    }
                    maxLength={2}
                    className={cn(
                      "mt-1",
                      getFieldError("state", contactData.state) &&
                        "border-red-500",
                    )}
                  />
                  {getFieldError("state", contactData.state) && (
                    <p className="text-sm text-red-500 mt-1">
                      {getFieldError("state", contactData.state)}
                    </p>
                  )}
                </div>

                <div>
                  <Label
                    htmlFor="contact-zip-code"
                    className="text-sm font-medium"
                  >
                    ZIP Code{reqMark(addrReq)}
                  </Label>
                  <Input
                    id="contact-zip-code"
                    type="text"
                    value={contactData.zipCode}
                    onChange={(e) =>
                      !useFixedList &&
                      handleContactChange("zipCode", e.target.value)
                    }
                    disabled={useFixedList}
                    onFocus={() =>
                      setFocusedFields((prev) => new Set(prev).add("zipCode"))
                    }
                    placeholder={
                      useFixedList
                        ? selectFromListPlaceholder
                        : "XXXXX or XXXXX-XXXX"
                    }
                    className={cn(
                      "mt-1",
                      getFieldError("zipCode", contactData.zipCode) &&
                        "border-red-500",
                    )}
                  />
                  {getFieldError("zipCode", contactData.zipCode) && (
                    <p className="text-sm text-red-500 mt-1">
                      {getFieldError("zipCode", contactData.zipCode)}
                    </p>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      );
    }
    case "url": {
      const hasErrors = field.state.errors && field.state.errors.length > 0;
      const shouldShowErrors =
        hasErrors && (forceValidation || focusedFields.has("url"));
      return (
        <UrlAnswerField
          value={field.state.value}
          allowMultiple={allowMultipleUrls}
          error={shouldShowErrors ? field.state.errors[0] : null}
          onChange={handleChange}
          onBlur={() => {
            field.handleBlur();
            setFocusedFields((prev) => new Set(prev).add("url"));
          }}
        />
      );
    }
    case "document": {
      return (
        <DocumentUploadField
          field={field}
          handleChange={handleChange}
          questionName={questionName}
          forceValidation={forceValidation}
          allowMultiple={allowMultipleDocuments}
        />
      );
    }
    case "photo": {
      return (
        <PhotoCaptureField
          value={field.state.value}
          onChange={handleChange}
          allowMultiple={allowMultiplePhotos}
          uploadFile={async (file) => {
            const { uploadAnswerFile } = await import(
              "@/hera-api/client/sdk.gen"
            );
            const resp = await uploadAnswerFile({
              body: { file },
            });
            const token = (resp as { data?: { token?: string } }).data?.token;
            if (!token) {
              throw new Error("Upload failed: no token returned");
            }
            return token;
          }}
        />
      );
    }
    default: {
      return null;
    }
  }
}

// Keep the old function for backward compatibility, but mark it as deprecated
/**
 * @deprecated Use QuestionField component instead. This function violates React's Rules of Hooks.
 */
export function renderQuestionField(
  answerType: LicenseQuestionAnswerType,
  field: any,
  answerOptions?: Array<SelectOption> | null,
  ref?: React.RefObject<HTMLInputElement | null>,
) {
  console.warn(
    "renderQuestionField is deprecated. Use QuestionField component instead to avoid hooks violations.",
  );

  // For backward compatibility, we'll create a simple wrapper
  // but this should be replaced with the proper component usage
  return (
    <QuestionField
      answerType={answerType}
      field={field}
      answerOptions={answerOptions}
      ref={ref}
    />
  );
}

export function validatorForQuestionType(
  questionType: LicenseQuestionAnswerType,
  isRequired: boolean,
  selectFromFixedList?: boolean,
  nacRequirements?: ContactRequirementValues,
  allowMultipleUrls = false,
  allowMultipleDocuments = false,
): z.ZodTypeAny {
  if (questionType === "url") {
    return urlAnswerSchema(isRequired, allowMultipleUrls);
  }

  if (questionType === "confirmation") {
    return z.literal(true, {
      errorMap: () => ({ message: "Confirmation is required" }),
    });
  }

  if (
    questionType === "name_address_contact" ||
    questionType === "company_contact"
  ) {
    const req =
      nacRequirements ??
      nacFieldRequirementsForValidation({
        answer_type: questionType,
        answer_required: isRequired,
      });
    return nacAnswerZodValidator(
      questionType,
      req,
      Boolean(selectFromFixedList),
    );
  }

  if (isRequired) {
    switch (questionType) {
      case "text":
      case "live_link":
        return z.string().min(1, "This field is required");
      case "address":
        return z
          .string()
          .min(1, "Address is required")
          .refine((val) => {
            try {
              const parsed = JSON.parse(val);
              const result = addressSchema.safeParse(parsed);
              if (!result.success) {
                return false;
              }
              // Additional validation for state and zip like in account form
              const { state, zipCode } = parsed;
              if (state && state.length !== 2) {
                return false;
              }
              if (zipCode && !/^\d{5}(-\d{4})?$/.test(zipCode)) {
                return false;
              }
              return true;
            } catch {
              return false;
            }
          }, "Invalid address format");
      case "number": {
        const numberValueSchema = z
          .number({
            required_error: "This field is required",
            invalid_type_error: "Please enter a valid number",
          })
          .refine((val) => Number.isFinite(val), "Please enter a valid number");
        const numberStringSchema = z
          .string({ required_error: "This field is required" })
          .refine((val) => val.trim().length > 0, "This field is required")
          .refine(
            (val) => !Number.isNaN(Number(val)),
            "Please enter a valid number",
          );
        return z.union([numberValueSchema, numberStringSchema]);
      }
      case "date":
        return z.string().min(1, "This field is required");
      case "single_select":
        return z.string().min(1, "This field is required");
      case "email":
        return z
          .string()
          .email("Invalid email format")
          .min(1, "Email is required");
      case "phone_number":
        return z
          .string()
          .regex(
            /^\(?[0-9]{3}\)?\s?[0-9]{3}\s?\-?\s?[0-9]{4}$/,
            "Please enter a valid US phone number",
          );
      case "multi_select":
        return z.array(z.string()).min(1, "Please select at least one option");
      case "boolean":
        // Require an explicit Yes/No; undefined/null must not coerce to false
        return z.boolean({
          required_error: "This field is required",
          invalid_type_error: "Please select Yes or No",
        });
      case "information":
        return z.string();
      case "document":
        return allowMultipleDocuments
          ? z
              .array(z.string().min(1))
              .min(1, "Upload at least one file")
              .nullable()
              .refine((val) => val != null && val.length > 0, {
                message: "Upload at least one file",
              })
          : z.string().min(1, "This field is required");
      case "photo":
        return z.custom<unknown>(hasPhotoAnswers, {
          message: "This field is required",
        });
      default:
        throw new Error(`Unknown question type: ${questionType}`);
    }
  }

  switch (questionType) {
    case "text":
    case "live_link":
      return z.string().optional();
    case "address":
      return z
        .string()
        .optional()
        .refine((val) => {
          if (!val) return true; // optional
          try {
            const parsed = JSON.parse(val);
            const result = addressSchema.safeParse(parsed);
            if (!result.success) {
              return false;
            }
            // Additional validation for state and zip like in account form
            const { state, zipCode } = parsed;
            if (state && state.length !== 2) {
              return false;
            }
            if (zipCode && !/^\d{5}(-\d{4})?$/.test(zipCode)) {
              return false;
            }
            return true;
          } catch {
            return false;
          }
        }, "Invalid address format");
    case "number": {
      const numberValueSchema = z
        .number({ invalid_type_error: "Please enter a valid number" })
        .refine((val) => Number.isFinite(val), "Please enter a valid number");
      const numberStringSchema = z
        .string()
        .refine(
          (val) => val.trim().length === 0 || !Number.isNaN(Number(val)),
          "Please enter a valid number",
        );
      return z.union([numberValueSchema, numberStringSchema]).optional();
    }
    case "date":
      return z.string().optional();
    case "single_select":
      return z.string().optional();
    case "email":
      return z.string().email("Invalid email format").optional();
    case "phone_number":
      return z
        .string()
        .regex(
          /^\(?[0-9]{3}\)?\s?[0-9]{3}\s?\-?\s?[0-9]{4}$/,
          "Please enter a valid US phone number",
        )
        .optional();
    case "multi_select":
      return z.array(z.string()).optional();
    case "boolean":
      return z.boolean().optional();
    case "information":
      return z.string().optional();
    case "document":
      return allowMultipleDocuments
        ? z.array(z.string()).nullish()
        : z.string().optional();
    case "photo":
      return z
        .custom<unknown>((val) => val == null || hasPhotoAnswers(val))
        .optional();
    default:
      throw new Error(`Unknown question type: ${questionType}`);
  }
}
